import { Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Youtube, Instagram, Facebook, Mail, MapPin, ArrowUpRight, Send, Check, Loader2 } from "lucide-react";
import { useState } from "react";
import { z } from "zod";
import { Logo } from "./Logo";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";

const socials = [
  { name: "YouTube", icon: Youtube, href: "https://youtube.com/@SyncTechPolska", color: "from-red-500 to-rose-600" },
  { name: "Instagram", icon: Instagram, href: "https://instagram.com/synctech.pl", color: "from-fuchsia-500 via-pink-500 to-orange-400" },
  { name: "Facebook", icon: Facebook, href: "https://www.facebook.com/share/1CXam7CJ9r/?mibextid=wwXIfr", color: "from-blue-600 to-blue-500" },
];

const emailSchema = z
  .string()
  .trim()
  .min(1, "E-mail jest wymagany.")
  .email("Podaj poprawny adres e-mail.")
  .max(255);

export function Footer() {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      toast.error("Sprawdź adres e-mail", {
        description: parsed.error.issues[0]?.message ?? "Niepoprawny e-mail.",
      });
      return;
    }
    if (!consent) {
      toast.error("Wymagana zgoda", {
        description: "Zaznacz zgodę marketingową, aby się zapisać.",
      });
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("/api/public/newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: parsed.data,
          source: "footer-newsletter",
          consent_marketing: true,
          consent_url: typeof window !== "undefined" ? window.location.href : undefined,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || `HTTP ${res.status}`);
      }
      setDone(true);
      setEmail("");
      setConsent(false);
      toast.success("Dzięki za zapis!", {
        description: "Sprawdź skrzynkę — wysłałem powitalnego maila 📬",
      });
      setTimeout(() => setDone(false), 5000);
    } catch (err) {
      console.error("[newsletter] subscribe failed:", err);
      toast.error("Coś poszło nie tak", {
        description: "Spróbuj jeszcze raz za chwilę.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="relative overflow-hidden border-t border-border bg-card/40">
      {/* Animated background blobs */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-40 left-1/4 h-96 w-96 rounded-full bg-primary/15 blur-3xl" />
        <div className="absolute -bottom-40 right-1/4 h-96 w-96 rounded-full bg-primary-glow/15 blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,color-mix(in_oklab,var(--primary)_8%,transparent)_1px,transparent_1px),linear-gradient(to_bottom,color-mix(in_oklab,var(--primary)_8%,transparent)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_at_center,black,transparent_70%)] opacity-40" />
      </div>

      {/* CTA banner */}
      <div className="border-b border-border/60">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-6 px-6 py-12 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="font-display text-2xl font-bold md:text-3xl">
              Gotowy na <span className="text-gradient">automatyzację sprzedaży?</span>
            </h3>
            <p className="mt-2 text-sm text-muted-foreground md:text-base">
              Bezpłatna konsultacja — 30 minut, konkretne odpowiedzi, zero zobowiązań.
            </p>
          </div>
          <a
            href="#kontakt"
            onClick={() => trackEvent("cta_book_call", { location: "footer", method: "form" })}
            className="group inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-glow transition-all hover:scale-105 hover:shadow-elegant"
          >
            Umów konsultację
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
          </a>
        </div>
      </div>

      {/* Main grid */}
      <div className="mx-auto grid max-w-7xl gap-12 px-6 py-16 md:grid-cols-12">
        {/* Brand block */}
        <div className="md:col-span-5">
          <Logo className="h-12 w-auto text-foreground" />
          <p className="mt-5 max-w-sm text-sm leading-relaxed text-muted-foreground">
            <span className="font-medium text-foreground">SyncTech</span> — wdrażam Baselinker i integruję ekosystem e-commerce.
            Indywidualne podejście do każdego projektu.
          </p>

          {/* Newsletter */}
          <form onSubmit={handleSubscribe} className="mt-6 max-w-sm">
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Otrzymuj insighty z e-commerce
            </label>
            <div className="group relative flex items-center overflow-hidden rounded-full border border-border bg-background/60 backdrop-blur transition-all focus-within:border-primary focus-within:shadow-glow">
              <Mail className="ml-4 h-4 w-4 shrink-0 text-muted-foreground" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="twoj@email.pl"
                disabled={loading || done}
                className="flex-1 bg-transparent px-3 py-3 text-sm outline-none placeholder:text-muted-foreground disabled:opacity-60"
                maxLength={255}
              />
              <button
                type="submit"
                disabled={loading || done}
                className="m-1 inline-flex h-9 items-center gap-1.5 rounded-full bg-primary px-4 text-xs font-semibold text-primary-foreground transition-all hover:bg-primary/90 active:scale-95 disabled:opacity-70"
              >
                {done ? (
                  <>
                    <Check className="h-3.5 w-3.5" />
                    Zapisano
                  </>
                ) : loading ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Zapisuję
                  </>
                ) : (
                  <>
                    <Send className="h-3.5 w-3.5" />
                    Zapisz
                  </>
                )}
              </button>
            </div>
            <label className="mt-3 flex items-start gap-2.5 px-1 text-[11px] leading-relaxed text-muted-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                disabled={loading || done}
                required
                className="mt-0.5 h-3.5 w-3.5 shrink-0 cursor-pointer accent-primary"
                aria-describedby="newsletter-consent-text"
              />
              <span id="newsletter-consent-text">
                Wyrażam zgodę na przetwarzanie mojego adresu e-mail przez SyncTech w celu otrzymywania newslettera marketingowego. Zgodę mogę wycofać w każdej chwili. Szczegóły w{" "}
                <Link to="/polityka-prywatnosci" className="underline transition hover:text-foreground">
                  Polityce prywatności
                </Link>.
              </span>
            </label>
            <p className="mt-2 px-2 text-[11px] text-muted-foreground">
              Zero spamu. Wypisanie 1 kliknięciem w każdej wiadomości.
            </p>
          </form>

          {/* Social icons */}
          <div className="mt-8">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Dołącz do społeczności
            </p>
            <div className="flex flex-wrap gap-3">
              {socials.map((s, i) => (
                <motion.a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.name}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.4 }}
                  whileHover={{ y: -4, scale: 1.08 }}
                  whileTap={{ scale: 0.95 }}
                  className="group relative grid h-12 w-12 place-items-center overflow-hidden rounded-2xl border border-border bg-card/80 backdrop-blur transition-colors hover:border-transparent"
                >
                  <span
                    className={`absolute inset-0 -z-10 bg-gradient-to-br ${s.color} opacity-0 transition-opacity duration-300 group-hover:opacity-100`}
                  />
                  <s.icon className="h-5 w-5 transition-colors duration-300 group-hover:text-white" />
                </motion.a>
              ))}
            </div>
          </div>
        </div>

        {/* Platforms */}
        <div className="md:col-span-3">
          <h4 className="mb-4 text-sm font-bold uppercase tracking-wider">Platformy</h4>
          <ul className="space-y-2.5 text-sm">
            {[
              { to: "/platformy/baselinker", label: "Baselinker" },
              { to: "/platformy/sellasist", label: "SellAsist" },
              { to: "/platformy/shoper", label: "Shoper" },
              { to: "/platformy/shopgold", label: "ShopGold" },
              { to: "/platformy/shopify", label: "Shopify" },
              { to: "/platformy/prestashop", label: "PrestaShop" },
            ].map((p) => (
              <li key={p.to}>
                <Link
                  to={p.to}
                  className="group inline-flex items-center gap-1.5 text-muted-foreground transition-colors hover:text-primary"
                >
                  <span className="relative">
                    {p.label}
                    <span className="absolute -bottom-0.5 left-0 h-px w-0 bg-primary transition-all duration-300 group-hover:w-full" />
                  </span>
                  <ArrowUpRight className="h-3 w-3 -translate-x-1 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                </Link>
              </li>
            ))}
          </ul>

          <h4 className="mb-4 mt-8 text-sm font-bold uppercase tracking-wider">Wiedza</h4>
          <ul className="space-y-2.5 text-sm">
            <li>
              <Link to="/blog" className="text-muted-foreground transition-colors hover:text-primary">
                Blog
              </Link>
            </li>
            <li>
              <Link to="/porownanie" className="text-muted-foreground transition-colors hover:text-primary">
                Porównania platform
              </Link>
            </li>
          </ul>
        </div>

        {/* Contact */}
        <div className="md:col-span-4" id="kontakt-footer">
          <h4 className="mb-4 text-sm font-bold uppercase tracking-wider">Kontakt</h4>
          <ul className="space-y-3 text-sm">
            <li>
              <a
                href="mailto:kontakt@synctech.pl"
                className="group flex items-start gap-3 text-muted-foreground transition-colors hover:text-foreground"
              >
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary transition-all group-hover:bg-primary group-hover:text-primary-foreground">
                  <Mail className="h-4 w-4" />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-wider opacity-70">E-mail</span>
                  <span className="font-medium text-foreground">kontakt@synctech.pl</span>
                </span>
              </a>
            </li>
            <li>
              <div className="flex items-start gap-3 text-muted-foreground">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
                  <MapPin className="h-4 w-4" />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-wider opacity-70">Lokalizacja</span>
                  <span className="font-medium text-foreground">Polska — pracuję zdalnie</span>
                </span>
              </div>
            </li>
          </ul>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-border/60">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-6 py-6 text-xs text-muted-foreground md:flex-row">
          <p>© {new Date().getFullYear()} SyncTech. Wszelkie prawa zastrzeżone.</p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <Link to="/polityka-prywatnosci" className="transition hover:text-foreground">
              Polityka prywatności
            </Link>
            <Link to="/regulamin" className="transition hover:text-foreground">
              Regulamin
            </Link>
            <button
              type="button"
              onClick={() => {
                const fn = (window as unknown as { synctechOpenCookieSettings?: () => void }).synctechOpenCookieSettings;
                fn?.();
              }}
              className="transition hover:text-foreground"
            >
              Ustawienia cookies
            </button>
            <span className="flex items-center gap-1.5">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
              </span>
              <span>Dostępny dla nowych projektów</span>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
